<script setup lang="ts">
import DivTable from '@/components/vuetify-custom-components/DivTable.vue'
import { useDisplay } from 'vuetify'
import {
  ChangeRequestActionRequiredItem,
  ChangeRequestTableOptions,
  ChangeRequestTableSorting,
} from '@/models/changeRequest'
import { $getChangeRequestActionRequiredItems } from '@/api/changeRequests'
import { isDateOverdue } from '@/helpers/utils/isDateOverdue'
import { usePriority } from '@/helpers/composables/usePriority'

interface IProps {
  cardTitle?: string,
  initialItemsPerPage?: number,
}

const props = withDefaults(defineProps<IProps>(), {
  cardTitle: 'Change Request Items Assigned To You',
  initialItemsPerPage: 10,
})

interface CustomEvents {
}

const emit = defineEmits<CustomEvents>()

const vuetifyDisplay = useDisplay

const {
  getPriorityColor
} = usePriority()

const router = useRouter()

const changeRequestTableHeaders = [
  { key: 'code', title: 'Code', sortable: true },
  { key: 'task_description', title: 'Description', sortable: true },
  { key: 'cr_name', title: 'CR Name', sortable: true },
  { key: 'cr_client_reference', title: 'CR Client Reference', sortable: true },
  { key: 'date_created', title: 'Date Created', sortable: true },
  { key: 'activity_required', title: 'Activity Required', sortable: true },
  { key: 'project_type_display', title: 'Project Type', sortable: true },
  { key: 'project_name', title: 'Project', sortable: true },
  { key: 'assigned_to_users', title: 'Assigned To', sortable: true },
  { key: 'status_display', title: 'Status', sortable: true },
  { key: 'priority_display', title: 'Priority', sortable: true },
  { key: 'requestor_name', title: 'Requestor', sortable: true },
  { key: 'due_date', title: 'Due Date', sortable: true },
  { key: 'file_attachments', title: 'File Attachments', sortable: false },
]

const changeRequestTableOptions = ref<ChangeRequestTableOptions>({
  itemsPerPage: props.initialItemsPerPage,
  page: 1,
  sortBy: []
})

const itemsPerPageOptions = [
  { value: 10, title: '10' },
  { value: 15, title: '15' },
  { value: 20, title: '20' },
  { value: 50, title: '50' },
]

const isMounted = ref<boolean>(false)

const changeRequests = ref<ChangeRequestActionRequiredItem[]>([])
const changeRequestsCount = ref<number>(0)
const changeRequestsLoading = ref<boolean>(false)

// View Change Request Dialog
const showViewChangeRequestDialog = ref<boolean>(false)
const selectedChangeRequestId = ref<null | number>(null)

// View Change Request Approval Dialog
const showViewChangeRequestApprovalDialog = ref<boolean>(false)
const selectedChangeRequestApprovalId = ref<null | number>(null)

// View Change Request Dialog Subtask
const showViewChangeRequestSubtaskDialog = ref<boolean>(false)
const selectedChangeRequestSubtaskId = ref<null | number>(null)

const gerOrdering = () => {
  // Define default ordering
  let ordering = undefined

  // Check if sortBy is defined and has at least one item
  if (changeRequestTableOptions.value.sortBy.length > 0) {
    const sortBy = changeRequestTableOptions.value.sortBy[0]
    const sortByKey = sortBy.key
    const sortOrder = sortBy.order === 'desc' ? '-' : '' // Determine the sorting order

    ordering = `${sortOrder}${sortByKey}`
  }

  return ordering
}

const getChangeRequests = async () => {
  changeRequestsLoading.value = true

  const ordering = gerOrdering()

  try {
    const res = await $getChangeRequestActionRequiredItems({
      page: changeRequestTableOptions.value.page,
      per_page: changeRequestTableOptions.value.itemsPerPage,
      ordering: ordering,
    })
    changeRequests.value = res.data.results
    changeRequestsCount.value = res.data.count
  } catch (e: any) {
    // If page doesn't exist goes to 1 page
    if (e?.response?.status === 404) {
      changeRequestTableOptions.value.page = 1
      await getChangeRequests()
    } else {
      console.log(e)
    }
  } finally {
    changeRequestsLoading.value = false
  }
}

onMounted(() => {
  getChangeRequests()

  isMounted.value = true
})

const onUpdateTableOptions = async ({ page, itemsPerPage, sortBy }: { page: number; itemsPerPage: number; sortBy: ChangeRequestTableSorting[] }) => {
  changeRequestTableOptions.value.page = page
  changeRequestTableOptions.value.itemsPerPage = itemsPerPage
  changeRequestTableOptions.value.sortBy = sortBy
  if (isMounted.value) {
    await getChangeRequests()
  }
}

const handleRowClick = (changeRequestItem: ChangeRequestActionRequiredItem) => {
  if (changeRequestItem?.activity_required === 'Change Request' && changeRequestItem?.id) {
    selectedChangeRequestId.value = changeRequestItem.id
    showViewChangeRequestDialog.value = true
  }
  else if (changeRequestItem?.activity_required === 'Approval' && changeRequestItem?.id) {
    selectedChangeRequestApprovalId.value = changeRequestItem.id
    showViewChangeRequestApprovalDialog.value = true
  }
  else if (changeRequestItem?.activity_required === 'Subtask' && changeRequestItem?.id) {
    selectedChangeRequestSubtaskId.value = changeRequestItem.id
    showViewChangeRequestSubtaskDialog.value = true
  }
}
</script>

<template>
  <VCard 
    variant="outlined"
    class="card-with-shadow"
    :title="cardTitle"
  >
    <ViewChangeRequestDialog
      v-model="showViewChangeRequestDialog"
      :change-request-id="selectedChangeRequestId || undefined"
    />
    <ViewChangeRequestApprovalDialog
      v-model="showViewChangeRequestApprovalDialog"
      :change-request-approval-id="selectedChangeRequestApprovalId || undefined"
      @onUpdateApproval="getChangeRequests"
    />
    <ViewChangeRequestSubtaskDialog
      v-model="showViewChangeRequestSubtaskDialog"
      :change-request-subtask-id="selectedChangeRequestSubtaskId || undefined"
      @onUpdateSubtask="getChangeRequests"
    />
    <VCardText>
      <VOverlay
        v-model="changeRequestsLoading"
        persistent
        absolute
        contained
        class="align-center justify-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VOverlay>
      <DivTable
        v-model:page="changeRequestTableOptions.page"
        :items="[ ...changeRequests ]"
        :items-length="parseInt(String(changeRequestsCount), 10)"
        :headers="changeRequestTableHeaders"
        :loading="!!changeRequestsLoading"
        grid-id="home-action-required-table"
        grid-storage-id="home-action-required-list"
        no-data-text="Change Requests not found"
        hover
        :items-per-page-options="itemsPerPageOptions"
        :items-per-page="changeRequestTableOptions.itemsPerPage"
        :sort-by="changeRequestTableOptions.sortBy"
        @update:options="onUpdateTableOptions"
      >
        <template #item.code="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.code }}
          </div>
        </template>
        <template #item.task_description="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.task_description }}
          </div>
        </template>
        <template #item.cr_name="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.cr_name }}
          </div>
        </template>
        <template #item.cr_client_reference="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.cr_client_reference }}
          </div>
        </template>
        <template #item.date_created="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.date_created }}
          </div>
        </template>
        <template #item.activity_required="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.activity_required }}
          </div>
        </template>
        <template #item.project_type_display="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.project_type_display }}
          </div>
        </template>
        <template #item.project_name="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.project_name }}
          </div>
        </template>
        <template #item.assigned_to_users="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.assigned_to_users }}
          </div>
        </template>
        <template #item.status_display="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.status_display }}
          </div>
        </template>
        <template #item.priority_display="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            <VChip :color="getPriorityColor(item.raw?.priority)">
              {{ item.raw?.priority_display }}
            </VChip>
          </div>
        </template>
        <template #item.requestor_name="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            {{ item.raw?.requestor_name }}
          </div>
        </template>
        <template #item.due_date="{ item }">
          <div class="cursor-pointer w-100" @click="handleRowClick(item.raw)">
            <div :class="{ 'text-primary': isDateOverdue(item.raw?.due_date) }">
              {{ item.raw?.due_date }}
            </div>
          </div>
        </template>
        <template #item.file_attachments="{ item }">
          <div class="cursor-auto">
            <div v-if="Array.isArray(item.raw?.file_attachments)">
              <a
                v-for="file in item.raw.file_attachments"
                :key="file.id"
                :href="file.file"
                target="_blank"
                @click.stop
              >
                <VIcon icon="tabler-file-description" />
              </a>
            </div>
            <div v-else>
              -
            </div>
          </div>
        </template>
      </DivTable>
    </VCardText>
  </VCard>
</template>
